import { useEffect, useState } from "react";
import { Loader2, Save, Plus, Trash2, Languages, HelpCircle, Info } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useTranslation } from "@/i18n";
import { fetchAdminContent, updateContent, translateTexts } from "@/lib/siteContent";
import { toast } from "sonner";

const LANGS = ["fi", "en", "sv"];

function uid() {
  return (typeof crypto !== "undefined" && crypto.randomUUID)
    ? crypto.randomUUID()
    : `c-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

const emptyI18n = () => ({ fi: "", en: "", sv: "" });
const emptyInfo = () => ({ id: uid(), title: emptyI18n(), body: emptyI18n() });
const emptyFaq = () => ({ id: uid(), question: emptyI18n(), answer: emptyI18n() });

// One field rendered across the 3 languages (Finnish source first).
function TriLangField({ label, value, onChange, multiline, testidBase }) {
  const Comp = multiline ? Textarea : Input;
  return (
    <div className="space-y-1.5">
      <Label className="text-xs uppercase tracking-wide text-slate-500">{label}</Label>
      <div className="grid gap-2 sm:grid-cols-3">
        {LANGS.map((lg) => (
          <div key={lg} className="space-y-1">
            <span className="text-[10px] font-semibold uppercase text-slate-400">
              {lg}{lg === "fi" ? " ★" : ""}
            </span>
            <Comp
              value={value?.[lg] ?? ""}
              onChange={(e) => onChange({ ...value, [lg]: e.target.value })}
              rows={multiline ? 3 : undefined}
              className="text-sm"
              data-testid={`${testidBase}-${lg}`}
            />
          </div>
        ))}
      </div>
    </div>
  );
}

export default function ContentEditor() {
  const { t } = useTranslation();
  const [info, setInfo] = useState([]);
  const [faq, setFaq] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [translatingId, setTranslatingId] = useState(null);

  useEffect(() => {
    fetchAdminContent()
      .then((c) => { setInfo(c.additionalInfo || []); setFaq(c.faq || []); })
      .catch((e) => toast.error(e.response?.data?.detail || e.message))
      .finally(() => setLoading(false));
  }, []);

  const updateList = (setter, id, patch) =>
    setter((list) => list.map((it) => (it.id === id ? { ...it, ...patch } : it)));

  // Draft EN/SV from the Finnish source of one item. Confirms before overwriting
  // any existing non-empty translation.
  const draftItem = async (kind, item) => {
    const fields = kind === "info"
      ? [{ key: "title", i18n: item.title }, { key: "body", i18n: item.body }]
      : [{ key: "question", i18n: item.question }, { key: "answer", i18n: item.answer }];

    if (!fields.some((f) => (f.i18n?.fi || "").trim())) {
      toast.error(t("admin.content.needFinnish"));
      return;
    }
    const hasExisting = fields.some((f) => (f.i18n?.en || "").trim() || (f.i18n?.sv || "").trim());
    if (hasExisting && !window.confirm(t("admin.content.overwriteConfirm"))) return;

    setTranslatingId(item.id);
    try {
      const texts = fields.map((f) => f.i18n?.fi || "");
      const tr = await translateTexts(texts, ["en", "sv"]);
      const patch = {};
      fields.forEach((f, i) => {
        patch[f.key] = { ...f.i18n, en: tr.en?.[i] ?? f.i18n.en, sv: tr.sv?.[i] ?? f.i18n.sv };
      });
      const setter = kind === "info" ? setInfo : setFaq;
      updateList(setter, item.id, patch);
      toast.success(t("admin.content.drafted"));
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally {
      setTranslatingId(null);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const saved = await updateContent({ additionalInfo: info, faq });
      setInfo(saved.additionalInfo || []);
      setFaq(saved.faq || []);
      toast.success(t("admin.content.saved"));
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="py-20 flex justify-center" data-testid="content-loading"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;
  }

  return (
    <div className="space-y-8" data-testid="admin-content-editor">
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm text-slate-600 max-w-2xl">{t("admin.content.intro")}</p>
        <Button onClick={save} disabled={saving} className="rounded-full h-10 bg-slate-900 hover:bg-slate-800 shrink-0" data-testid="admin-content-save">
          {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Save className="h-4 w-4 mr-1.5" />}{t("admin.content.save")}
        </Button>
      </div>

      {/* Additional Information */}
      <section className="space-y-4" data-testid="admin-content-info">
        <div className="flex items-center gap-2">
          <Info className="h-4 w-4 text-slate-700" />
          <h3 className="font-display text-base font-semibold">{t("admin.content.infoTitle")}</h3>
        </div>
        {info.map((it) => (
          <div key={it.id} className="rounded-2xl border border-border bg-card p-4 space-y-4" data-testid={`admin-content-info-item-${it.id}`}>
            <TriLangField label={t("admin.content.fieldTitle")} value={it.title} multiline={false}
              onChange={(v) => updateList(setInfo, it.id, { title: v })} testidBase={`content-info-title-${it.id}`} />
            <TriLangField label={t("admin.content.fieldBody")} value={it.body} multiline
              onChange={(v) => updateList(setInfo, it.id, { body: v })} testidBase={`content-info-body-${it.id}`} />
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" className="rounded-full" onClick={() => draftItem("info", it)} disabled={translatingId === it.id} data-testid={`content-info-draft-${it.id}`}>
                {translatingId === it.id ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <Languages className="h-3.5 w-3.5 mr-1.5" />}{t("admin.content.draftBtn")}
              </Button>
              <Button variant="ghost" size="sm" className="rounded-full text-red-600 hover:text-red-700" onClick={() => setInfo((l) => l.filter((x) => x.id !== it.id))} data-testid={`content-info-remove-${it.id}`}>
                <Trash2 className="h-3.5 w-3.5 mr-1.5" />{t("admin.content.remove")}
              </Button>
            </div>
          </div>
        ))}
        <Button variant="outline" className="rounded-full" onClick={() => setInfo((l) => [...l, emptyInfo()])} data-testid="admin-content-add-info">
          <Plus className="h-4 w-4 mr-1.5" />{t("admin.content.addInfo")}
        </Button>
      </section>

      {/* FAQ */}
      <section className="space-y-4" data-testid="admin-content-faq">
        <div className="flex items-center gap-2">
          <HelpCircle className="h-4 w-4 text-slate-700" />
          <h3 className="font-display text-base font-semibold">{t("admin.content.faqTitle")}</h3>
        </div>
        {faq.map((it) => (
          <div key={it.id} className="rounded-2xl border border-border bg-card p-4 space-y-4" data-testid={`admin-content-faq-item-${it.id}`}>
            <TriLangField label={t("admin.content.fieldQuestion")} value={it.question} multiline={false}
              onChange={(v) => updateList(setFaq, it.id, { question: v })} testidBase={`content-faq-question-${it.id}`} />
            <TriLangField label={t("admin.content.fieldAnswer")} value={it.answer} multiline
              onChange={(v) => updateList(setFaq, it.id, { answer: v })} testidBase={`content-faq-answer-${it.id}`} />
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" className="rounded-full" onClick={() => draftItem("faq", it)} disabled={translatingId === it.id} data-testid={`content-faq-draft-${it.id}`}>
                {translatingId === it.id ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <Languages className="h-3.5 w-3.5 mr-1.5" />}{t("admin.content.draftBtn")}
              </Button>
              <Button variant="ghost" size="sm" className="rounded-full text-red-600 hover:text-red-700" onClick={() => setFaq((l) => l.filter((x) => x.id !== it.id))} data-testid={`content-faq-remove-${it.id}`}>
                <Trash2 className="h-3.5 w-3.5 mr-1.5" />{t("admin.content.remove")}
              </Button>
            </div>
          </div>
        ))}
        <Button variant="outline" className="rounded-full" onClick={() => setFaq((l) => [...l, emptyFaq()])} data-testid="admin-content-add-faq">
          <Plus className="h-4 w-4 mr-1.5" />{t("admin.content.addFaq")}
        </Button>
      </section>
    </div>
  );
}
